import { ref, computed } from 'vue';
import type { Branch } from '@/entities/Branch';
import { useUserStore } from '@/entities/User/@x/Branch';
import api, { API_BASE_URL } from '@/shared/api';
import { getCurrentSelectedBranch } from './selectedBranchStorage';
import { selectBranchForAppearance } from './selectBranchForAppearance';

const DEFAULT_LOGO = '/assets/htm-logo.svg';
const LOGO_STREAM_PATH = '/logo/stream';

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const logoCache = ref<Map<number, string>>(new Map());
const failedBranchIds = ref<Set<number>>(new Set());
const refreshTrigger = ref(0);
const refreshGeneration = ref(0);
const urlGenerations = new Map<number, number>();

const isBranchLogoUrl = (url: string): boolean => {
  return url.includes(LOGO_STREAM_PATH);
};

const buildLogoStreamUrl = (branchId: number): string => {
  const logoUrl = `${API_BASE_URL}/branches/${branchId}${LOGO_STREAM_PATH}?v=${Date.now()}`;
  logoCache.value.set(branchId, logoUrl);
  urlGenerations.set(branchId, refreshGeneration.value);

  return logoUrl;
};

const isLogoFailed = (branchId: number): boolean => {
  return failedBranchIds.value.has(branchId);
};

const selectBranchForLogo = (): Branch | null => {
  try {
    const userStore = useUserStore();

    return selectBranchForAppearance({
      accessibleBranches: userStore.accessibleBranches,
      branches: userStore.branches,
      savedBranchId: getCurrentSelectedBranch(),
    }).branch;
  } catch (error) {
    console.warn('Branch selection failed:', error);
    return null;
  }
};

/**
 * Composable for managing branch logos
 * Provides logo URLs, caching, and refresh capabilities
 */
export function useBranchLogo() {
  const currentBranch = computed<Branch | null>(() => {
    const refreshVersion = refreshTrigger.value;

    try {
      return refreshVersion >= 0 ? selectBranchForLogo() : null;
    } catch (error) {
      console.warn('Current branch computation failed:', error);
      return null;
    }
  });

  const currentLogoUrl = computed<string>(() => {
    const refreshVersion = refreshTrigger.value;
    const branch = refreshVersion >= 0 ? currentBranch.value : null;

    if (!branch?.logoPath || isLogoFailed(branch.id)) {
      return DEFAULT_LOGO;
    }

    const cached = logoCache.value.get(branch.id);
    if (cached) {
      return cached;
    }

    return buildLogoStreamUrl(branch.id);
  });

  const getBranchLogoUrl = (branch: Branch): string => {
    if (!branch.logoMinioObjectName || isLogoFailed(branch.id)) {
      return DEFAULT_LOGO;
    }

    const cached = logoCache.value.get(branch.id);
    if (cached) {
      return cached;
    }

    return buildLogoStreamUrl(branch.id);
  };

  const wasBuiltBeforeLastRefresh = (branchId: number): boolean => {
    const builtAtGeneration = urlGenerations.get(branchId);

    return builtAtGeneration !== undefined && builtAtGeneration < refreshGeneration.value;
  };

  const markLogoAsFailed = (branchId: number) => {
    const canRetryWithRefreshedToken = wasBuiltBeforeLastRefresh(branchId);

    logoCache.value.delete(branchId);
    urlGenerations.delete(branchId);

    if (!canRetryWithRefreshedToken) {
      failedBranchIds.value.add(branchId);
    }

    refreshTrigger.value++;
  };

  const handleLogoError = () => {
    const branch = currentBranch.value;
    if (branch) {
      markLogoAsFailed(branch.id);
    }
  };

  const retryFailedLogos = (): boolean => {
    refreshGeneration.value++;

    if (failedBranchIds.value.size === 0) {
      return false;
    }

    failedBranchIds.value.clear();
    logoCache.value.clear();
    refreshTrigger.value++;

    return true;
  };

  const clearBranchLogoCache = (branchId: number) => {
    logoCache.value.delete(branchId);
    failedBranchIds.value.delete(branchId);
    urlGenerations.delete(branchId);
    refreshTrigger.value++;
  };

  const clearAllLogoCache = () => {
    logoCache.value.clear();
    failedBranchIds.value.clear();
    urlGenerations.clear();
    refreshTrigger.value++;
  };

  const refreshCurrentLogo = () => {
    const branch = currentBranch.value;
    if (branch) {
      clearBranchLogoCache(branch.id);
    }
    refreshTrigger.value++;
  };

  const preloadUserLogos = async () => {
    try {
      const userStore = useUserStore();
      await userStore.me();
      clearAllLogoCache();
    } catch (error) {
      console.warn('Could not preload user logos:', error);
    }
  };

  const refreshLogosAfterBranchChange = () => {
    clearAllLogoCache();
    refreshTrigger.value++;
  };

  const resolvePrintableLogoSrc = async (): Promise<string> => {
    const logoUrl = currentLogoUrl.value;
    if (!isBranchLogoUrl(logoUrl)) {
      return logoUrl;
    }

    try {
      // logoUrl already carries API_BASE_URL, and a relative base such as '/api' would be
      // prefixed again by the api client, fetching /api/api/... on QA and on-premise.
      const response = await api.get<Blob>(logoUrl.slice(API_BASE_URL.length), {
        responseType: 'blob',
      });
      return await blobToDataUrl(response.data);
    } catch {
      return DEFAULT_LOGO;
    }
  };

  return {
    // State
    currentBranch,
    currentLogoUrl,
    // Methods
    getBranchLogoUrl,
    handleLogoError,
    isBranchLogoUrl,
    isLogoFailed,
    markLogoAsFailed,
    retryFailedLogos,
    clearBranchLogoCache,
    clearAllLogoCache,
    refreshCurrentLogo,
    preloadUserLogos,
    refreshLogosAfterBranchChange,
    resolvePrintableLogoSrc,
    // Constants
    DEFAULT_LOGO,
  };
}

export default useBranchLogo;
